import React, { useState, useEffect, useRef } from 'react';
import './Home.css';
import { Link, useLocation } from 'react-router-dom';

const EMAIL = 'misha.ignatyev01@gmail.com';

const sectionHeadingStyle = {
  textAlign: 'center',
  marginBottom: '3rem',
  color: '#ffffff',
  textShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
  fontSize: '2.5rem',
};

function Home() {
  const [copied, setCopied] = useState(false);
  const projectsRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#projects' && projectsRef.current) {
      projectsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const copyEmail = () => {
    navigator.clipboard.writeText(EMAIL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Project data for each category
  const ubcProjects = [
    { id: 'automated-microscore', name: 'Automated Microscore', img: '/images/automated-microscore/project-preview.PNG' },
    { id: 'eledigm-venture', name: 'Eledigm Venture', img: '/images/eledigm-venture/Screenshot%202026-02-15%20at%2000.41.33.png' },
    { id: 'cad', name: 'Computer Aided Design Projects', img: '/images/CAD-projects/Screenshot%202026-02-15%20at%2000.04.57.png' },
  ];
  const uwcProjects = [
    { id: 'mini-synthesizer', name: 'Mini-Synthesizer', img: '/images/mini-synthesizer/Mini_pic.jpg' },
    { id: 'rainwater-harvesting', name: 'Rainwater Harvesting System', img: '/images/rainwater-harvesting/rainwater.png' },
  ];

  return (
    <>
      <div className="home-container">
        <div className="home-main-row">
        <div className="home-left">
          <h1 className="home-name">Mikhail Ignatyev</h1>
          <p className="home-intro">
            I am a Manufacturing Engineering graduate from the University of British Columbia focused on mechanical system design, experimental validation, and hardware development. I specialize in bridging early-stage concept development, analytical modeling, and real-world prototyping to create reliable, manufacturable mechanical systems.
          </p>
          <p className="home-intro">
            My experience spans research-driven engineering and high-volume manufacturing, where I have redesigned production equipment, analyzed structural performance, and developed hardware solutions that improved system reliability and manufacturability. I thrive in collaborative, cross-functional environments, and some of my best work has come from partnering closely with process, production, and design teams to solve complex problems together. I'm also passionate about leveraging AI and computational tools to enhance engineering workflows, from design optimization to data-driven decision-making.
          </p>
          <p className="home-intro">
            I enjoy working across design, simulation, and hands-on experimentation to develop robust engineering solutions for R&D and advanced manufacturing applications.
          </p>
          <div className="home-focus-areas">
            <div style={{ color: '#00d4ff', fontWeight: 600, fontSize: '1rem', marginBottom: '0.6rem' }}>Focus Areas</div>
            <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#e0e0e0', fontSize: '0.95rem', lineHeight: 1.7, maxWidth: 540 }}>
              <li>Mechanical Hardware Development & Mechanism Design</li>
              <li>Experimental Testing, Prototyping & Design Validation</li>
              <li>Structural Performance & Failure Investigation</li>
              <li>Design for Manufacturability & Production Scaling</li>
              <li>Advanced Fabrication & Manufacturing Technologies</li>
            </ul>
          </div>
        </div>
        <div className="home-right">
          <img src="/images/face_pic.jpg" alt="Mikhail Ignatyev" className="profile-img" />
          <div className="social-row">
            <a href="https://github.com/ManuCapstone1/OpticalModule" target="_blank" rel="noopener noreferrer" className="social-icon">
              <img src="/images/github-mark-white.svg" alt="GitHub" />
            </a>
            <a href="https://www.linkedin.com/in/mikhail-ignatyev/" target="_blank" rel="noopener noreferrer" className="social-icon">
              <img src="/images/linkedin-icon.svg" alt="LinkedIn" />
            </a>
            <div className="email-box">
              <a href={`mailto:${EMAIL}`} className="email-box-text" aria-label="Email">📧 {EMAIL}</a>
              <button type="button" onClick={copyEmail} className="email-copy-btn" aria-label="Copy email">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <Link
            to="/#projects"
            className="btn primary"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              window.history.pushState(null, '', '/#projects');
            }}
            style={{ marginTop: '1rem' }}
          >
            View My Work
          </Link>
        </div>
        </div>
      </div>

      {/* Industry Projects Section */}
      <section style={{ width: '100%', marginTop: '4rem', marginBottom: '4rem' }}>
        <h1 style={sectionHeadingStyle}>Industry Projects</h1>
        <div style={{ maxWidth: '960px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'flex-start',
            background: 'linear-gradient(135deg, #23243a 60%, #1a1a1a 100%)',
            borderRadius: '1.2rem',
            boxShadow: '0 4px 24px rgba(0, 212, 255, 0.13)',
            border: '2px solid rgba(0, 212, 255, 0.13)',
            padding: '1.5rem',
            flexWrap: 'wrap',
          }}>
            <a href="https://www.tesla.com/" target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, display: 'block' }}>
              <img src="/images/key-experiences/Tesla_logo.png" alt="Tesla" style={{ width: 120, height: 120, objectFit: 'contain', background: '#fff', borderRadius: '12px', padding: '0.5rem' }} />
            </a>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ color: '#00d4ff', fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.1rem' }}>Equipment Engineer | Tesla Inc. | Sep 2023 – Aug 2024</div>
              <div style={{ color: '#b0d4e8', fontWeight: 600, marginBottom: '0.4rem', fontSize: '1.05rem' }}>Windshield Lift Assist Redesign Project</div>
              <p style={{ color: '#e0e0e0', margin: 0, lineHeight: 1.6, fontSize: '1rem' }}>Led a year-long equipment redesign of the windshield glazing station, one of the most cost-sensitive operations in General Assembly. Rebuilt the system to eliminate chronic over-cycling, significantly improving wet-out quality and reducing scrap on the most expensive discarded component in the department. The redesign eliminated 90 minutes of monthly downtime, achieved four consecutive months of zero unplanned downtime, and drove a 2% increase in OEE. Collaborated cross-functionally with process and production teams to resolve persistent glass cracking during decking, bringing the crack rate to zero within three months.</p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'flex-start',
            background: 'linear-gradient(135deg, #23243a 60%, #1a1a1a 100%)',
            borderRadius: '1.2rem',
            boxShadow: '0 4px 24px rgba(0, 212, 255, 0.13)',
            border: '2px solid rgba(0, 212, 255, 0.13)',
            padding: '1.5rem',
            flexWrap: 'wrap',
          }}>
            <a href="https://www.gea.com/en/" target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, display: 'block' }}>
              <img src="/images/key-experiences/GEA_logo.jpg" alt="GEA" style={{ width: 120, height: 120, objectFit: 'contain', background: '#fff', borderRadius: '12px', padding: '0.5rem' }} />
            </a>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ color: '#00d4ff', fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.1rem' }}>Mechanical R&D Engineer Intern | GEA Freezing Inc. | May 2023 – Sept 2023</div>
              <div style={{ color: '#b0d4e8', fontWeight: 600, marginBottom: '0.4rem', fontSize: '1.05rem' }}>Orbital Welder Project</div>
              <p style={{ color: '#e0e0e0', margin: 0, lineHeight: 1.6, fontSize: '1rem' }}>Led an R&D initiative to transition coil welding operations from manual to orbital welding, conducting extensive research and iterative experimentation to optimize the process for production readiness. Through systematic testing of tungsten electrode diameters, sharpening angles, and power source upgrades, improved tungsten tip longevity from just 1 weld per insert to 13-20 welds before replacement. Each weld configuration was validated through hydrostatic expansion testing to ensure joint integrity. The optimized process was projected to cut annual production time by 45%, representing a major step toward scalable, repeatable weld quality across the freezer product line.</p>
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            alignItems: 'flex-start',
            background: 'linear-gradient(135deg, #23243a 60%, #1a1a1a 100%)',
            borderRadius: '1.2rem',
            boxShadow: '0 4px 24px rgba(0, 212, 255, 0.13)',
            border: '2px solid rgba(0, 212, 255, 0.13)',
            padding: '1.5rem',
            flexWrap: 'wrap',
          }}>
            <a href="https://www.ksmlaser.com/" target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0, display: 'block' }}>
              <img src="/images/key-experiences/KSM_logo_updated.png" alt="KSM" style={{ width: 120, height: 120, objectFit: 'cover', background: '#fff', borderRadius: '12px', padding: '0.5rem' }} />
            </a>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ color: '#00d4ff', fontWeight: 700, marginBottom: '0.5rem', fontSize: '1.1rem' }}>Laser Programmer Intern | KSM Stainless Steel Ltd. | June 2022 – Sept 2022</div>
              <div style={{ color: '#b0d4e8', fontWeight: 600, marginBottom: '0.4rem', fontSize: '1.05rem' }}>Production Workflow Optimization</div>
              <p style={{ color: '#e0e0e0', margin: 0, lineHeight: 1.6, fontSize: '1rem' }}>Identified inefficiencies across the production workflow and led a process improvement initiative spanning laser programming and shop floor systems. Developed optimized nesting layouts for two TRUMPF laser systems to maximize material utilization and reduce scrap across mild steel, stainless steel, and aluminum jobs. On the operational side, replaced a paper-based inventory tracking system with a digital Excel solution and restructured the job costing workflow, cutting quote turnaround time by 25%. The combined improvements streamlined the full order lifecycle from quoting through production, resulting in faster throughput and reduced material waste.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Academic & Entrepreneurial Projects Section */}
      <section>
      <h1 id="projects" ref={projectsRef} style={{ ...sectionHeadingStyle, marginBottom: '1rem', scrollMarginTop: '5rem' }}>Academic & Entrepreneurial Projects</h1>
      <div style={{ width: '100%', marginTop: 0 }}>
        <h2 style={{ color: '#00d4ff', fontSize: '2rem', marginBottom: '0.2rem', textAlign: 'center', textShadow: '0 0 10px #00d4ff44' }}>
          University of British Columbia
        </h2>
        <div style={{ color: '#e0e0e0', fontSize: '1.1rem', marginBottom: '2.2rem', textAlign: 'center' }}>
          BASc in Manufacturing Engineering (2020-2025)
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1rem',
          maxWidth: '960px',
          margin: '0 auto 3.5rem auto',
          justifyContent: 'center',
        }}>
          {ubcProjects.map(project => (
            <Link
              to={`/projects/${project.id}`}
              key={project.id}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{
                position: 'relative',
                background: 'linear-gradient(135deg, #23243a 60%, #1a1a1a 100%)',
                borderRadius: '1.2rem',
                boxShadow: '0 4px 24px rgba(0, 212, 255, 0.13)',
                border: '2px solid rgba(0, 212, 255, 0.13)',
                padding: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'transform 0.18s, box-shadow 0.18s',
                cursor: 'pointer',
                width: '100%',
              }}>
                {project.id === 'automated-microscore' && (
                  <div style={{
                    position: 'absolute',
                    top: '0.75rem',
                    left: '0.75rem',
                    background: 'linear-gradient(135deg, #e63946, #c1121f)',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '0.35rem 0.6rem',
                    borderRadius: '1rem',
                    boxShadow: '0 2px 8px rgba(230, 57, 70, 0.5)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    zIndex: 1,
                  }}>
                    Research Paper In Review
                  </div>
                )}
                <img
                  src={project.img}
                  alt={project.name + ' preview'}
                  style={{
                    width: '100%',
                    aspectRatio: 1,
                    objectFit: 'cover',
                    objectPosition: 'center',
                    borderRadius: '1rem',
                    marginBottom: '0.5rem',
                    background: '#222',
                    transition: 'transform 0.2s',
                  }}
                />
                <h3 style={{
                  color: '#00d4ff',
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  margin: 0,
                  lineHeight: 1.2,
                }}>{project.name}</h3>
              </div>
            </Link>
          ))}
        </div>

        <h2 style={{ color: '#00d4ff', fontSize: '2rem', marginBottom: '0.2rem', textAlign: 'center', textShadow: '0 0 10px #00d4ff44' }}>
          UWC South East Asia (Singapore)
        </h2>
        <div style={{ color: '#e0e0e0', fontSize: '1.1rem', marginBottom: '2.2rem', textAlign: 'center' }}>
          High School (2016-2020)
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1rem',
          maxWidth: '960px',
          margin: '0 auto 3.5rem auto',
          justifyContent: 'center',
        }}>
          {uwcProjects.map(project => (
            <Link
              to={`/projects/${project.id}`}
              key={project.id}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{
                background: 'linear-gradient(135deg, #23243a 60%, #1a1a1a 100%)',
                borderRadius: '1.2rem',
                boxShadow: '0 4px 24px rgba(0, 212, 255, 0.13)',
                border: '2px solid rgba(0, 212, 255, 0.13)',
                padding: '0.5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'transform 0.18s, box-shadow 0.18s',
                cursor: 'pointer',
                width: '100%',
              }}>
                <img
                  src={project.img}
                  alt={project.name + ' preview'}
                  style={{
                    width: '100%',
                    aspectRatio: 1,
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    marginBottom: '0.5rem',
                    background: '#222',
                    transition: 'transform 0.2s',
                  }}
                />
                <h3 style={{
                  color: '#00d4ff',
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  textAlign: 'center',
                  margin: 0,
                  lineHeight: 1.2,
                }}>{project.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
      </section>
    </>
  );
}

export default Home; 